<template>
  <div class="hoisting-collapse-content workpermit-content-slot">
    <HoistingDetail
      :showOperate="showOperate"
      :showPrint="showPrint"
      :isEdit="isEdit"
      :isProjectAvailable="isProjectAvailable"
      :hoistingData="permitData"
      :floorsOptions="floorsOptions"
      :isPPSuspened="isPPSuspened"
      :operationCb="operationCb"
    />

    <!-- isolated device recommendation -->
    <div
      v-if="isolatedDevicesRecommendation"
      class="workpermit-content-slot__review-box"
    >
      <strong>{{ $t('workPermit.isolatedDeviceRecommendation') }}</strong>
      <div class="last-update">
        <div
          v-for="(text, index) in isolatedDevicesRecommendation"
          :key="text"
          class="tender-font"
        >
          {{ index+1 }}. {{ text }}
        </div>
      </div>
    </div>

    <!-- after office hours review info -->
    <div
      v-if="afterOfficeHoursReviewedInfo.who && isHKlandUser"
      class="workpermit-content-slot__review-box"
    >
      <strong>{{ $t('workPermit.commentsfromReviewer') }}</strong>
      <div class="last-update">
        <div class="last-update__text">
          {{ $t('workPermit.reviewedBy',[afterOfficeHoursReviewedInfo.who, afterOfficeHoursReviewedInfo.when]) }}
        </div>
      </div>
      <pre>{{ afterOfficeHoursReviewedInfo.content | isEmptyValue }}</pre>
    </div>

    <!-- reject info -->
    <div
      v-if="rejectInfo && rejectInfo.who"
      class="workpermit-content-slot__review-box"
    >
      <strong>{{ rejectInfoSubject }}</strong>
      <div class="last-update">
        <div class="last-update__text">
          {{ $t(hoistingRejectBylineKey,[rejectInfo.who, rejectInfo.when]) }}
        </div>
      </div>
      <pre>{{ rejectInfo.content | isEmptyValue }}</pre>
    </div>

    <!-- comments from approver view -->
    <div
      v-if="approveInfo && approveInfo.who"
      class="workpermit-content-slot__review-box"
    >
      <strong>{{ $t('workPermit.commentsfromApprover') }}</strong>
      <div class="last-update">
        <div class="last-update__text">
          {{ $t(hoistingApproveBylineKey,[approveInfo.who, approveInfo.when]) }}
        </div>
      </div>
      <pre>{{ approveInfo.content | isEmptyValue }}</pre>
    </div>

    <!-- within office hrs first step  -->
    <div
      v-if="showOneStepWorkflow"
      class="workpermit-content-slot__review-box"
    >
      <div class="workpermit-content-slot__recommendation">
        <p class="workpermit-content-slot__recommendation_hint">
          {{ $t('workPermit.plsInputRecommendationToHoisting') }}
        </p>
        <IsolatedDeviceOptions ref="isoDeviceOptions" />
        <div class="workpermit-content-slot__btns">
          <p @click="openPermitsAtLocation()">
            <img
              src="/ic_permits_at_location.png"
              :alt="permitsAtLocationLabel"
            >
            <span>{{ permitsAtLocationLabel }} ({{ permitsAtSameLocationCount }})</span>
          </p>
          <button
            class="btn btn--darkblue"
            @click="openRejectModal()"
          >
            {{ $t('action.rejectAction') }}
          </button>
          <button
            class="btn btn--orange"
            @click="openApproveModal()"
          >
            {{ $t('action.approveAction') }}
          </button>
        </div>
      </div>
    </div>
    <!-- after office hrs first step -->
    <template v-if="showTwoStepsWorkflowFirstStep">
      <div class="workpermit-content-slot__review-box">
        <div class="workpermit-content-slot__recommendation">
          <p class="workpermit-content-slot__recommendation_hint">
            {{ $t('workPermit.plsInputRecommendationToHoisting') }}
          </p>
          <IsolatedDeviceOptions ref="isoDeviceOptions" />
          <div class="workpermit-content-slot__btns">
            <p @click="openPermitsAtLocation()">
              <img
                src="/ic_permits_at_location.png"
                :alt="permitsAtLocationLabel"
              >
              <span>{{ permitsAtLocationLabel }} ({{ permitsAtSameLocationCount }})</span>
            </p>
            <button
              class="btn btn--darkblue"
              @click="openRejectModal()"
            >
              {{ $t('action.rejectAction') }}
            </button>
            <button
              class="btn btn--orange"
              @click="openNextReviewerModal()"
            >
              {{ $t('tenantProjects.submission.nextReviewer') }}
            </button>
          </div>
        </div>
      </div>
      <WorkPermNextReviewerModal
        :ref="`${WP_ACTION_TYPE.HPApprove}_nextReviewerModal_${permitData.id}`"
        :modalId="`${WP_ACTION_TYPE.HPApprove}_nextReviewerModal_${permitData.id}-${+new Date()}`"
        modalType="medium-modal"
        @multi-select-submited="confirmNextReviewer"
      >
        <div slot="wp-next-reviewer-content">
          <div class="workpermit-content-slot__comments-box">
            <TextField
              v-hkldinput
              class="workpermit-content-slot__comments-textarea"
              :value="comments"
              :readonly="false"
              :required="false"
              :showColon="false"
              :label="$t('typeComments')"
              @updateValue="val => comments = val"
            />
          </div>
        </div>
      </WorkPermNextReviewerModal>
    </template>
    <!-- after office hrs second step -->
    <div
      v-if="showTwoStepsWorkflowSecondStep"
      class="workpermit-content-slot__review-box"
    >
      <div class="workpermit-content-slot__recommendation">
        <div class="workpermit-content-slot__btns">
          <p @click="openPermitsAtLocation()">
            <img
              src="/ic_permits_at_location.png"
              :alt="permitsAtLocationLabel"
            >
            <span>{{ permitsAtLocationLabel }} ({{ permitsAtSameLocationCount }})</span>
          </p>
          <button
            class="btn btn--darkblue"
            @click="openRejectModal()"
          >
            {{ $t('action.rejectAction') }}
          </button>
          <button
            class="btn btn--orange"
            @click="openApproveModal()"
          >
            {{ $t('action.approveAction') }}
          </button>
        </div>
      </div>
    </div>

    <!-- when APPR , BMO can start hoisting -->
    <div
      v-if="showHoistBox && canBmoHoist"
      :key="`isolated-info-${permitData.id}`"
      :class="['workpermit-content-slot__review-box_row', `isolated-info-${permitData.id}`]"
    >
      <div class="isolated-info">
        <InputField
          class="small-3 medium-3 large-3 condition column"
          :label="$t('workPermit.isolatedBy')"
          :showColon="false"
          :required="true"
          @updateValue="val => isolatedBy = val"
        />
        <div class="small-6 medium-6 large-6 condition column">
          <p class="required-flag date-time-selector-title">
            {{ $t('workPermit.isolatedTime') }}
          </p>
          <DateTimeSelector
            :showClearBtn="true"
            :setMinTime="true"
            :required="true"
            @updateDate="val => isolatedTime = val"
          />
        </div>
        <button
          class="btn--darkblue"
          @click="startHoist"
        >
          {{ $t('workPermit.hoist') }}
        </button>
      </div>
    </div>
    <div
      v-if="isolatedInfo.content"
      :class="['workpermit-content-slot__review-box_row']"
    >
      <div class="isolated-info">
        <div class="updatedBy">
          <p>{{ $t('workPermit.isolatedBy') }}</p>
          <p>{{ isolatedInfo.content && isolatedInfo.content.isolatedBy }}</p>
        </div>
        <div class="updatedTime">
          <p>{{ $t('workPermit.isolatedTime') }}</p>
          <p>{{ isolatedInfo.content && dateFormat(isolatedInfo.content.isolatedTime) }}</p>
        </div>
        <div>
          <i class="gray-font">{{ $t(hoistingActionFooterBylineKey, [isolatedInfo.who, isolatedInfo.when]) }}</i>
        </div>
      </div>
    </div>

    <div
      v-if="showReHoistBox && canBmoHoist"
      :key="`checked-info-${permitData.id}`"
      :class="['workpermit-content-slot__review-box_row', `checked-info-${permitData.id}`]"
    >
      <div class="checked-info">
        <InputField
          class="small-3 medium-3 large-3 condition column"
          :label="$t('workPermit.deHoistedBy')"
          :showColon="false"
          :required="true"
          @updateValue="val => checkedBy = val"
        />
        <div class="small-6 medium-6 large-6 condition column">
          <p class="required-flag date-time-selector-title">
            {{ $t('workPermit.deHoistedTime') }}
          </p>
          <DateTimeSelector
            :showClearBtn="true"
            :setMinTime="true"
            :required="true"
            @updateDate="val => checkedTime = val"
          />
        </div>
        <button
          class="btn--darkblue"
          @click="completeHoist"
        >
          {{ $t('workPermit.deHoist') }}
        </button>
      </div>
    </div>
    <div
      v-if="checkedInfo.content"
      :class="['workpermit-content-slot__review-box_row']"
    >
      <div class="checked-info">
        <div class="updatedBy">
          <p>{{ $t('workPermit.deHoistedBy') }}</p>
          <p>{{ checkedInfo.content && checkedInfo.content.checkedBy }}</p>
        </div>
        <div class="updatedTime">
          <p>{{ $t('workPermit.deHoistedTime') }}</p>
          <p>{{ checkedInfo.content && dateFormat(checkedInfo.content.checkedTime) }}</p>
        </div>
        <div>
          <i class="gray-font">{{ $t(hoistingActionFooterBylineKey, [checkedInfo.who, checkedInfo.when]) }}</i>
        </div>
      </div>
    </div>
    <pre
      v-if="showHint"
      class="hoisting__hint"
      v-html="$t(`termsAndConditions.hoistingHintText.${regionName}`)"
    />
    <Modal
      :ref="`hoistingModal_${permitData.id}`"
      :modalId="`hoistingModal-${permitData.id}-${+new Date()}`"
      modalType="medium-modal"
      :beforeOpenModal="beforeOpenModal"
      :beforeCloseModal="beforeCloseModal"
    >
      <p
        class="font-bold"
        :class="{ 'required-flag': modalStatus === $t('action.rejectAction')}"
      >
        {{ hoistingActionConfirmHint }}
      </p>
      <div
        :id="`hp-comments-${permitData.id}`"
        class="workpermit-content-slot__comments-box"
      >
        <TextField
          v-hkldinput
          :placeholder="$t('typeComments')"
          class="workpermit-content-slot__comments-textarea"
          :value="comments"
          :readonly="false"
          :required="true"
          @updateValue="val => comments = val"
        />
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="closeModal"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          v-if="modalStatus === $t('action.approveAction')"
          class="btn btn--orange"
          @click="approve"
        >
          {{ $t('action.approveAction') }}
        </button>
        <button
          v-if="modalStatus === $t('action.rejectAction')"
          class="btn btn--orange"
          @click="reject"
        >
          {{ $t('action.rejectAction') }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { toRefs, computed, unref } from 'vue-demi'
import DateTimeSelector from '@/controls/DateTimeSelector'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import Modal from '@/controls/Modal'
import InputField from '@/controls/InputField'
import TextField from '@/controls/TextField'
import mutationTypes from '@/store/mutation-types'
import { sendEmail } from '@/api/postman/request'
import { utils as formUtils } from '@/plugin/InputChange'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { EmailTypes } from '@/utils/consts'
import { tmsPermsTypes } from '@/utils/aclHelper'
import { useStoreGetters } from '@/store/index'
import { useIsProjectRepresentative } from '@/pages/projects/projectInfo'
import { disableTenantContractorEdit } from './utils'
import { buildWorkPermitHistoryDetail, WORK_PERMIT_COMMENT_MODE } from './commentFlow'
import { WORK_PERMIT_STATUS, WORK_PERMIT_TYPES, WP_ACTION_TYPE } from './consts'
import { canExternalViewerSeeHoistingHistory } from './history/index'
import {
  getHoistingHistoryCreatedAt,
  getHoistingHistoryDetail,
  getHoistingInfoByStatus,
  getIsolatedHoistingInfo,
  getLastHoistingDisplayHistoryByStatus,
  parseStructuredHoistingActionContent,
  resolveHoistingRejectCommentSubjectKey,
  resolveRejectedByAfterOfficeHoursReviewerOnly,
} from './hoistingHistoryDisplay'
import WorkPermNextReviewerModal from './WorkPermNextReviewerModal'
import IsolatedDeviceOptions from './IsolatedDeviceOptions'
import HoistingDetail from './HoistingDetail'
import i18n from '@/utils/i18n'
import workPermitCollapseContentMixin from './workPermitCollapseContentMixin'
import { getHoistingConfirmHint, getPermitsAtLocationLabel } from './regionalWorkPermitLabels'

export default {
  name: 'HoistingCollapseContent',
  components: {
    Modal,
    HoistingDetail,
    TextField,
    InputField,
    DateTimeSelector,
    IsolatedDeviceOptions,
    WorkPermNextReviewerModal,
  },
  mixins: [workPermitCollapseContentMixin],
  props: {
    permitData: {
      type: Object,
      default: () => {},
      required: true,
    },
    floorsOptions: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    isProjectAvailable: {
      type: Boolean,
      default: false,
    },
    isPPSuspened: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const regionsStore = useStoreGetters('regions', ['regionName'])
    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])
    const isProjectRep = useIsProjectRepresentative()
    const {
      permitData: permitDataRef,
    } = toRefs(props)
    const showHint = computed(() => {
      const hasI18nTxt = i18n.te(`termsAndConditions.hoistingHintText.${unref(regionsStore.regionName)}`)
      const isCmsHoisting = !unref(permitDataRef).standalone && !unref(permitDataRef).isTenantProject
      const notDraft = unref(permitDataRef).status !== WORK_PERMIT_STATUS.DRAFT
      const allowedGuys = unref(isHKlandUser) || (isCmsHoisting && unref(isProjectRep))
      return notDraft && allowedGuys && hasI18nTxt
    })
    return {
      showHint,
      isHKlandUser,
      ...regionsStore,
    }
  },
  data () {
    return {
      comments: '',
      modalStatus: '',
      isolatedBy: '',
      isolatedTime: '',
      checkedBy: '',
      checkedTime: '',
      isIsolateErr: false,
      isCheckedErr: false,
      commentsValidate: false,
      WP_ACTION_TYPE,
      permitsAtSameLocationCount: 0,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit']),
    ...mapGetters('tenantProject', ['currentTenantProject']),
    parent () {
      return this.currentWorkPermit
    },
    innerPermitData () {
      return this.permitData
    },
    showOperate () {
      if (!this.isProjectAvailable) return false
      if (disableTenantContractorEdit()) return false
      return this.permitData.status === WORK_PERMIT_STATUS.DRAFT || this.permitData.status === WORK_PERMIT_STATUS.REJECTED
    },
    showPrint () {
      const printStatus = [
        WORK_PERMIT_STATUS.APPR,
        WORK_PERMIT_STATUS.INPROGRESS,
        WORK_PERMIT_STATUS.COMPLETED,
        WORK_PERMIT_STATUS.EXPIRED,
        WORK_PERMIT_STATUS.CANCELLED,
      ]
      if (this.permitData.status === WORK_PERMIT_STATUS.SUSPENDED) {
        return printStatus.includes(this.permitData.statusBeforeSuspend)
      } else {
        return printStatus.includes(this.permitData.status)
      }
    },
    parentStatusValid () {
      const parentStatus = [
        WORK_PERMIT_STATUS.APPR,
        WORK_PERMIT_STATUS.ENDED,
        WORK_PERMIT_STATUS.CANCELLED,
        WORK_PERMIT_STATUS.SUSPENDED,
      ]
      return parentStatus.includes(this.currentWorkPermit.status)
    },
    pendingForFirstStep () {
      const allowStatus = [WORK_PERMIT_STATUS.WAPPR]
      return this.parentStatusValid && allowStatus.includes(this.permitData.status)
    },
    pendingForSecondStep () {
      const allowStatus = [WORK_PERMIT_STATUS.REVIEWED]
      return this.parentStatusValid && allowStatus.includes(this.permitData.status)
    },
    showCommentsFromApprover () {
      return this.showRejectBox || this.showApprovedComments
    },
    showApprovedComments () {
      return !lodash.isEmpty(this.approveInfo)
    },
    showRejectBox () {
      return this.permitData.status === WORK_PERMIT_STATUS.REJECTED || (this.permitData.status === WORK_PERMIT_STATUS.DRAFT && !lodash.isEmpty(this.rejectInfo))
    },
    showHoistBox () {
      const showStatus = [WORK_PERMIT_STATUS.APPR]
      return showStatus.includes(this.permitData.status)
    },
    showReHoistBox () {
      const allowStatus = [WORK_PERMIT_STATUS.INPROGRESS]
      return allowStatus.includes(this.permitData.status)
    },
    showOneStepWorkflow () {
      return (this.isWithinOfficeHours && this.pendingForFirstStep && this.canApproveWithinOfficeHoursHP) ||
        this.canCombineAfterOfficeHoursTwoStepsIntoOne
    },
    showTwoStepsWorkflowFirstStep () {
      return (!this.isWithinOfficeHours && this.pendingForFirstStep && this.canReviewAfterOfficeHoursHP) &&
        !this.canCombineAfterOfficeHoursTwoStepsIntoOne
    },
    showTwoStepsWorkflowSecondStep () {
      return !this.isWithinOfficeHours && this.pendingForSecondStep && this.canApproveAfterOfficeHoursHP
    },
    showPermitsAtSameLocation () {
      return this.showOneStepWorkflow || this.showTwoStepsWorkflowFirstStep || this.showTwoStepsWorkflowSecondStep
    },
    isWithinOfficeHours () {
      const startDate = this.permitData.startDate
      const endDate = this.permitData.endDate
      return dateUtil.isWithinOfficeHours(startDate, endDate)
    },
    canApproveWithinOfficeHoursHP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.approveOfficeHP)
    },
    canReviewAfterOfficeHoursHP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.reviewNonOfficeHP)
    },
    canApproveAfterOfficeHoursHP () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.approveNonOfficeHP)
    },
    canCombineAfterOfficeHoursTwoStepsIntoOne () {
      return !this.isWithinOfficeHours && this.canReviewAfterOfficeHoursHP && this.canApproveAfterOfficeHoursHP && this.pendingForFirstStep
    },
    canBmoHoist () {
      if (this.permitData.standalone) {
        return this.canProcessCurrentWP(tmsPermsTypes.workpermit.followUpHoisting)
      }
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.reviewPP) ||
        this.canProcessCurrentWP(tmsPermsTypes.workpermit.followUpHoisting)
    },
    showHoistingHistoryForViewer () {
      return canExternalViewerSeeHoistingHistory({
        isHKlandUser: this.isHKlandUser,
        parentStatus: lodash.get(this.currentWorkPermit, 'status'),
      })
    },
    /** Neutral "By … on …" for non–HK staff (matches WP external history bylines). */
    hoistingRejectBylineKey () {
      return this.isHKlandUser ? 'workPermit.rejectedBy' : 'workPermit.historyCommentByOn'
    },
    hoistingApproveBylineKey () {
      return this.isHKlandUser ? 'workPermit.apprBy' : 'workPermit.historyCommentByOn'
    },
    hoistingActionFooterBylineKey () {
      return this.isHKlandUser ? 'lastUpdatedBy' : 'workPermit.historyCommentByOn'
    },
    permitsAtLocationLabel () {
      return getPermitsAtLocationLabel()
    },
    hoistingActionConfirmHint () {
      const action = this.modalStatus === this.$t('action.approveAction')
        ? this.$t('action.approveAction')
        : this.$t('action.rejectAction')
      return getHoistingConfirmHint(action)
    },
    rejectInfo () {
      if (!this.showHoistingHistoryForViewer) return {}
      return this.getInfoByStatus(WORK_PERMIT_STATUS.REJECTED)
    },
    rejectInfoSubject () {
      const subjectKey = resolveHoistingRejectCommentSubjectKey({
        isWithinOfficeHours: this.isWithinOfficeHours,
        rejectRoles: lodash.get(this.rejectInfo, 'detail.roles'),
        rejectedByAfterOfficeHoursReviewerOnly: lodash.get(
          this.rejectInfo,
          'detail.rejectedByAfterOfficeHoursReviewerOnly',
        ),
      })
      return this.$t(subjectKey)
    },
    approveInfo () {
      if (!this.showHoistingHistoryForViewer) return {}
      return this.getInfoByStatus(WORK_PERMIT_STATUS.APPR)
    },
    isolatedDevicesRecommendation () {
      if (!this.showHoistingHistoryForViewer) return undefined
      const attributeName = 'detail.isolatedDevicesOptionsText'
      let result = lodash.get(this.rejectInfo, attributeName) || lodash.get(this.approveInfo, attributeName) || lodash.get(this.afterOfficeHoursReviewedInfo, attributeName)
      if (!lodash.isEmpty(result)) {
        result = result.split('\n')
      }
      return result
    },
    afterOfficeHoursReviewedInfo () {
      if (!this.showHoistingHistoryForViewer) return {}
      if (this.isWithinOfficeHours) return {}
      let reviewedInfo = this.getInfoByStatus(WORK_PERMIT_STATUS.REVIEWED)
      return reviewedInfo
    },
    isolatedInfo () {
      if (!this.showHoistingHistoryForViewer) return {}
      return this.getIsolatedInfo()
    },
    checkedInfo () {
      if (!this.showHoistingHistoryForViewer) return {}
      return this.getInfoByStatus(WORK_PERMIT_STATUS.COMPLETED)
    },
  },
  watch: {
    showPermitsAtSameLocation: {
      handler (newVal) {
        if (newVal) {
          this.countPermitsAtSameLocation()
        }
      },
      immediate: true,
    },
  },
  mounted () {
    if (this.selected) {
      this.scrollToHoisting()
    }
  },
  methods: {
    ...mapActions('workPermit', ['changeWorkPermitStatus', 'getWorkPermitById', 'getWorkPermitsInProjectByProjectId', 'getAllWorkPermits']),
    ...mapMutations('workPermit', {
      setCurrntWorkPermit: mutationTypes.WORKPERMIT_SET_CURRENT_WORKPERMIT,
    }),
    async confirmNextReviewer (nextReviewer) {
      const newHP = await this.updateStatus(WORK_PERMIT_STATUS.REVIEWED, null, nextReviewer.id)
      await sendEmail(EmailTypes.workPermits.hoistingNextReviewer, {...newHP, workType: this.parent.workType})
      formUtils.resetDirty()
    },
    async openNextReviewerModal () {
      const action = WP_ACTION_TYPE.HPApprove
      await this.$refs[`${action}_nextReviewerModal_${this.permitData.id}`].openModal(action, this.permitData)
    },
    getPermitsAtSameLocationQuery () {
      let building = lodash.get(this.currentWorkPermit, 'building.code')
      if (!building) {
        building = this.permitData.standalone
          ? lodash.get(this.currentWorkPermit, 'tenancy.building.code')
          : lodash.get(this.currentTenantProject, 'tenancy.building.code') ||
          lodash.get(this.currentWorkPermit, 'project.tenancy.building.code')
      }
      const floor = Array.isArray(this.innerPermitData.floors) ? this.innerPermitData.floors.join(', ') : null
      return {
        status: WORK_PERMIT_STATUS.APPR,
        type: WORK_PERMIT_TYPES.HOISTING,
        building: building,
        floors: floor,
        startDate: moment(this.innerPermitData.startDate).startOf('day').unix() * 1000,
        endDate: moment(this.innerPermitData.endDate).endOf('day').unix() * 1000,
      }
    },
    openPermitsAtLocation () {
      const query = this.getPermitsAtSameLocationQuery()
      const name = 'hoistingAtLocation'
      let routeData = this.$router.resolve({name, query})
      window.open(routeData.href, '_blank').focus()
    },
    async countPermitsAtSameLocation () {
      const query = this.getPermitsAtSameLocationQuery()
      const {status, type, building, floors, startDate, endDate} = query
      const permitsAtSameLocation = await this.getAllWorkPermits({
        filters: {
          type,
          building,
          floors,
          status,
          limitAttrs: true,
          viewAllStartDate: new Date(startDate),
          viewAllEndDate: new Date(endDate),
        },
      })
      this.permitsAtSameLocationCount = (permitsAtSameLocation && permitsAtSameLocation.length) || 0
    },
    async updateStatus (status, comments, nextReviewerId) {
      let isolatedDevicesOptionsText
      if (this.$refs['isoDeviceOptions']) {
        isolatedDevicesOptionsText = this.$refs['isoDeviceOptions'].getRecommendation()
      }
      const roles = { isAfterOfficeHoursReviewer: this.canReviewAfterOfficeHoursHP, isWithinOfficeHoursApprover: this.canApproveWithinOfficeHoursHP, isAfterOfficeHoursApprover: this.canApproveAfterOfficeHoursHP }
      const isStructuredPayload = comments !== null && typeof comments === 'object' && !Array.isArray(comments)
      let commentBlock
      if (!isStructuredPayload && (status === WORK_PERMIT_STATUS.APPR || status === WORK_PERMIT_STATUS.REJECTED)) {
        const rawText = comments != null && comments !== '' ? comments : this.comments
        commentBlock = buildWorkPermitHistoryDetail({
          mode: WORK_PERMIT_COMMENT_MODE.SOLE_EXTERNAL,
          toStatus: status,
          internalComment: '',
          externalComment: rawText,
        })
      } else {
        commentBlock = { comments: comments || this.comments }
      }
      const historyDetail = { ...commentBlock, isolatedDevicesOptionsText, roles }
      if (status === WORK_PERMIT_STATUS.REJECTED) {
        historyDetail.rejectedByAfterOfficeHoursReviewerOnly = resolveRejectedByAfterOfficeHoursReviewerOnly({
          isWithinOfficeHours: this.isWithinOfficeHours,
          rejectRoles: roles,
        })
      }
      const newHP = await this.changeWorkPermitStatus({
        status,
        historyDetail,
        id: this.permitData.id,
        updatedAt: this.permitData.updatedAt,
        nextReviewerId,
        refresh: false,
      })
      await this.getWorkPermitById(this.permitData.parentId)
      await this.getWorkPermitsInProjectByProjectId({ projectId: this.permitData.projectId })
      return newHP
    },
    validateReason (callback) {
      return !this.commentsValidate || this.validate(`#hp-comments-${this.permitData.id}`)
    },
    beforeOpenModal () {
      this.comments = ''
      return true
    },
    beforeCloseModal () {
      fusionInputCheckUtil.resetAllFields()
    },
    openModal (status, commentsValidate) {
      this.modalStatus = status
      this.commentsValidate = commentsValidate
      this.$refs[`hoistingModal_${this.permitData.id}`].open()
    },
    closeModal () {
      this.$refs[`hoistingModal_${this.permitData.id}`].close()
    },
    openRejectModal (commentsValidate = true) {
      this.openModal(this.$t('action.rejectAction'), commentsValidate)
    },
    openApproveModal (commentsValidate = false) {
      this.openModal(this.$t('action.approveAction'), commentsValidate)
    },

    async approve () {
      this.closeModal()
      const hoisting = await this.updateStatus(WORK_PERMIT_STATUS.APPR, this.comments)
      formUtils.resetDirty()
      await sendEmail(EmailTypes.workPermits.approveNotificationHP, {
        ...hoisting,
        location: this.parent.location,
        workType: this.parent.workType,
        tenancy: this.parent.tenancy,
      })
    },
    async reject () {
      if (!this.validateReason()) return
      this.closeModal()
      const newHP = await this.updateStatus(WORK_PERMIT_STATUS.REJECTED, this.comments)
      formUtils.resetDirty()
      await sendEmail(EmailTypes.workPermits.rejected, {
        ...newHP,
        location: this.parent.location,
        workType: this.parent.workType,
        tenancy: this.parent.tenancy,
      })
    },
    validate (clsName) {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, clsName)
      return fusionInputCheckUtil.isAllFieldsValid(clsName)
    },
    getHistoryDetail (history) {
      return getHoistingHistoryDetail(history)
    },
    getHistoryCreatedAt (history) {
      return getHoistingHistoryCreatedAt(history)
    },
    getLastDisplayHistoryByStatus (histories, status, startStatus = WORK_PERMIT_STATUS.WAPPR) {
      return getLastHoistingDisplayHistoryByStatus(histories, status, startStatus)
    },
    getInfoByStatus (status) {
      return getHoistingInfoByStatus({
        permitData: this.permitData,
        status,
        dateFormat: this.dateFormat,
        emptyValue: this.$t('emptyValue'),
      })
    },
    parseStructuredHoistingActionContent (content) {
      return parseStructuredHoistingActionContent(content)
    },
    getIsolatedInfo () {
      return getIsolatedHoistingInfo({
        permitData: this.permitData,
        dateFormat: this.dateFormat,
        emptyValue: this.$t('emptyValue'),
      })
    },
    dateFormat (time, flag = true) {
      return dateUtil.formatDate(time, flag)
    },
    async startHoist () {
      if (this.validate(`.isolated-info-${this.permitData.id}`)) {
        formUtils.resetDirty()
        await this.updateStatus(WORK_PERMIT_STATUS.INPROGRESS, {
          isolatedBy: this.isolatedBy,
          isolatedTime: this.isolatedTime,
        })
      }
    },
    async completeHoist () {
      if (this.validate(`.checked-info-${this.permitData.id}`)) {
        formUtils.resetDirty()
        await this.updateStatus(WORK_PERMIT_STATUS.COMPLETED, {
          checkedBy: this.checkedBy,
          checkedTime: this.checkedTime,
        })
      }
    },
    scrollToHoisting () {
      setTimeout(() => {
        const element = document.getElementById(this.permitData.id)
        if (element) {
          window.scrollTo(0, element.offsetTop + 10)
          EventBus.emit('window_scroll')
        }
      }, 1200) // after the collapse css animation finished
    },
    operationCb (hoistingId, expandStatus) {
      this.$emit('expand-status', hoistingId, expandStatus)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/workpermit/style';
.modal-content {
  & ::v-deep {
    .header-panel {
      padding: 15px 0
    }
    .tenancy-table {
      margin: auto
    }
  }
}

.workpermit-content-slot {
  &__comments-textarea {
    & ::v-deep textarea {
      width: 100%;
    }
  }
}
.workpermit-content-slot__sprinkler_hint {
  input {
    vertical-align: middle;
  }
}
.gray-font {
  position: relative;
  top: 13px;
  font-size: 12px;
  color: #878787;
  height: 20px;
  display: block;
  margin: 5px 0;
}
.workpermit-content-slot__recommendation {
  & ::v-deep textarea {
    width: 100% !important;
    height: 70px !important;
    min-height: 70px !important;
  }
  .workpermit-content-slot__btns {
    justify-content: flex-end;
  }
  .workpermit-content-slot__btns {
    p {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 0;
      margin-right: 20px;
      cursor: pointer;
      span {
        text-decoration: underline;
      }
    }
  }
  .workpermit-content-slot__recommendation_hint {
    font-weight: bold;
    font-size: 14px;
  }
}
.hoisting__hint {
  text-align: right;
  color: #B88C00;
  font-weight: bold;
}
</style>
